import axios from "axios";
import configProvider from "config/configProvider";
import { GeocodeCandidate, GeocodeProximity } from "points/models";

/**
 * Address and place lookup for the add-a-point screen, called straight from the browser.
 *
 * This is the only call in the feature that does not go through the Flow BFF, and it is
 * deliberate. The Mapbox token is a public `pk.` key -- it is already embedded in every map this
 * app draws and is designed to be client-side. Proxying it through the BFF would add a hop and a
 * deployment dependency without hiding anything that is not already in the page.
 *
 * Nothing here is trusted as the final coordinate. A result seeds the pin; the user then drags it
 * to the actual gate or yard entrance and confirms it by eye. That is the whole reason the map is
 * draggable -- a rooftop-accurate address is still the wrong side of a rail yard.
 *
 * TWO APIS, ONE BOX. Mapbox splits what this screen needs across two endpoints:
 *
 *   - Geocoding v6 knows addresses, streets and towns. It has NO businesses at all -- v6 dropped
 *     the POI type. Searching it for a business returns streets that merely share a word
 *     ("McDonalds" matches McDonald Avenue), which looks like a hit and is not one.
 *   - Search Box knows businesses -- truck stops, motels, restaurants -- and is where every POI
 *     lives.
 *
 * Crews are dispatched to both kinds of place, so the screen asks BOTH and merges the answers
 * into one list rather than making the user first declare which kind of thing they are looking
 * for. Nobody typing a truck stop on a named road should have to classify it first.
 */

const ADDRESS_URL = "https://api.mapbox.com/search/geocode/v6/forward";
const POI_URL = "https://api.mapbox.com/search/searchbox/v1/forward";
const REVERSE_URL = "https://api.mapbox.com/search/geocode/v6/reverse";

/** Where Railcrew operates. Keeps results to places a crew could actually be driven to. */
const COUNTRIES = "us,ca,mx";

const PER_SOURCE_LIMIT = 5;

/** Ceiling on the merged list. Long enough to hold both kinds, short enough to scan. */
const MAX_CANDIDATES = 8;

/** Coordinate precision used to spot the same place arriving from both APIs (~1m). */
const DEDUPE_PRECISION = 5;

const firstString = (...values: any[]): string => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

/**
 * Both APIs nest the parsed address parts under properties.context, with the same keys but not
 * quite the same values: v6 reports region_code as "US-NE" while Search Box reports "NE". The
 * DB column holds two characters, so the last segment is taken either way.
 */
const readContext = (context: any) => ({
  city: firstString(context?.place?.name, context?.locality?.name),
  stateAbbreviation: firstString(context?.region?.region_code).split("-").pop() ?? "",
  zipCode: firstString(context?.postcode?.name),
  streetLine: firstString(
    context?.address?.name,
    [context?.address?.address_number, context?.address?.street_name].filter(Boolean).join(" "),
    context?.street?.name
  ),
});

/**
 * Strict numeric read. Number(null), Number("") and Number(false) are all 0 and all finite, so a
 * feature with a missing coordinate would otherwise parse as latitude 0 / longitude 0 -- Null
 * Island, which is the single value this entire screen exists to keep out of the table. A
 * missing coordinate has to read as missing, not as the equator.
 */
const toCoordinate = (value: any): number | null => {
  if (value === null || value === undefined || typeof value === "boolean") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const coordinatesOf = (feature: any): { latitude: number; longitude: number } | null => {
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const longitude = toCoordinate(coordinates[0]);
  const latitude = toCoordinate(coordinates[1]);
  if (latitude === null || longitude === null) return null;

  return { latitude, longitude };
};

/**
 * What a result actually IS, in the words a dispatcher would use.
 *
 * Search Box does not answer with businesses only -- it mixes POIs, towns and addresses into one
 * response -- so the badge cannot be derived from which API replied. It has to come from the
 * feature's own type, or a town ends up labelled as though it were a truck stop.
 */
export const friendlyType = (featureType: string): string => {
  switch (String(featureType ?? "").toLowerCase()) {
    case "poi":
      return "Business";
    case "address":
      return "Address";
    case "street":
      return "Street";
    case "place":
    case "locality":
      return "Town";
    case "neighborhood":
      return "Area";
    case "postcode":
      return "ZIP";
    case "region":
      return "State";
    default:
      return "Place";
  }
};

/** One result from Search Box -- usually a business, but towns and addresses arrive here too. */
export const toPoiCandidate = (feature: any, index: number): GeocodeCandidate | null => {
  const point = coordinatesOf(feature);
  if (!point) return null;

  const properties = feature.properties ?? {};
  const parts = readContext(properties.context);
  const featureType = firstString(properties.feature_type);
  const isPoi = featureType.toLowerCase() === "poi";
  const name = firstString(properties.name, properties.name_preferred);
  const where = firstString(properties.full_address, properties.place_formatted);

  return {
    id: firstString(properties.mapbox_id, feature.id, `poi-${index}`),
    // Ordering and the name offer both key off this, so it must mean "is a business", not
    // "came from the business API".
    kind: isPoi ? "poi" : "address",
    typeLabel: friendlyType(featureType),
    // The business name, kept separately so selecting a POI can name the point after it. Only a
    // real POI has one -- a town's name is not what anyone would call the location.
    name: isPoi ? name : "",
    // Name first: it is what was searched for, and what tells two identical-looking truck stops
    // apart. The address follows as the thing that says WHICH one.
    label: [name, where].filter(Boolean).join(" — ") || `${point.latitude}, ${point.longitude}`,
    ...point,
    // Search Box puts a POI's street line on `address`; context is the fallback.
    addressLine1: firstString(properties.address, parts.streetLine, name),
    city: parts.city,
    stateAbbreviation: parts.stateAbbreviation,
    zipCode: parts.zipCode,
  };
};

/** One address, street or town from Geocoding v6. */
export const toAddressCandidate = (feature: any, index: number): GeocodeCandidate | null => {
  const point = coordinatesOf(feature);
  if (!point) return null;

  const properties = feature.properties ?? {};
  const parts = readContext(properties.context);

  return {
    id: firstString(properties.mapbox_id, feature.id, `address-${index}`),
    kind: "address",
    typeLabel: friendlyType(firstString(properties.feature_type)),
    name: "",
    label: firstString(
      properties.full_address,
      [properties.name, properties.place_formatted].filter(Boolean).join(", "),
      `${point.latitude}, ${point.longitude}`
    ),
    ...point,
    // Fall back to the feature's own name when the address context is thin -- a place-level hit
    // ("North Platte") has no street, and an empty address line is worse than an approximate one
    // the user can correct by moving the pin.
    addressLine1: firstString(parts.streetLine, properties.name),
    city: parts.city,
    stateAbbreviation: parts.stateAbbreviation,
    zipCode: parts.zipCode,
  };
};

/**
 * Drops the same place arriving from both APIs, keeping whichever was listed first.
 *
 * POIs are listed first deliberately: someone who typed a business name wants the business, and
 * the street it sits on is the less specific answer to the same question.
 */
export const mergeCandidates = (
  poi: GeocodeCandidate[],
  address: GeocodeCandidate[]
): GeocodeCandidate[] => {
  const seen = new Set<string>();
  const merged: GeocodeCandidate[] = [];

  [...poi, ...address].forEach((candidate) => {
    const key = `${candidate.latitude.toFixed(DEDUPE_PRECISION)},${candidate.longitude.toFixed(
      DEDUPE_PRECISION
    )}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(candidate);
  });

  return merged.slice(0, MAX_CANDIDATES);
};

const searchAddresses = async (query: string): Promise<any[]> => {
  const response = await axios.get(ADDRESS_URL, {
    params: {
      q: query,
      access_token: configProvider.MapboxApiKey,
      country: COUNTRIES,
      limit: PER_SOURCE_LIMIT,
      language: "en",
    },
  });
  const features = response.data?.features;
  return Array.isArray(features) ? features : [];
};

const searchPois = async (query: string, proximity: string | null): Promise<any[]> => {
  const response = await axios.get(POI_URL, {
    params: {
      q: query,
      access_token: configProvider.MapboxApiKey,
      country: COUNTRIES,
      limit: PER_SOURCE_LIMIT,
      language: "en",
      ...(proximity ? { proximity } : {}),
    },
  });
  const features = response.data?.features;
  return Array.isArray(features) ? features : [];
};

/**
 * Looks up a place. Resolves to an empty array when neither API has a match -- a normal answer
 * for a yard name nothing knows, which the caller shows as such rather than as a failure. Throws
 * only when BOTH requests fail; one surviving source is still a usable answer.
 *
 * `proximity` biases the POI search, and it matters more than it looks. Brand names are the
 * common case here -- there are hundreds of each truck stop and motel chain -- so an unbiased
 * search answers with whichever the index happens to rank first, which is rarely the right
 * state. The pin is used when there is one; before that, the best address hit for the SAME query
 * stands in, so naming the town in the query is enough to pull the POI search to it.
 */
const search = async (
  query: string,
  proximity?: GeocodeProximity | null
): Promise<GeocodeCandidate[]> => {
  const trimmed = (query ?? "").trim();
  if (!trimmed) return [];

  let addressFeatures: any[] = [];
  let addressFailed = false;
  try {
    addressFeatures = await searchAddresses(trimmed);
  } catch {
    addressFailed = true;
  }

  const addressCandidates = addressFeatures
    .map(toAddressCandidate)
    .filter((c): c is GeocodeCandidate => c !== null);

  // The pin wins; otherwise borrow the town this same query resolved to.
  const bias = proximity ?? addressCandidates[0] ?? null;
  const proximityParam = bias ? `${bias.longitude},${bias.latitude}` : null;

  let poiCandidates: GeocodeCandidate[] = [];
  let poiFailed = false;
  try {
    poiCandidates = (await searchPois(trimmed, proximityParam))
      .map(toPoiCandidate)
      .filter((c): c is GeocodeCandidate => c !== null);
  } catch {
    poiFailed = true;
  }

  if (addressFailed && poiFailed) {
    throw new Error("Both address and place lookups failed.");
  }

  return mergeCandidates(poiCandidates, addressCandidates);
};

/**
 * What is at a coordinate.
 *
 * Dragging the pin, or typing a latitude and longitude, produces a place with no address -- and
 * the address fields are required and read-only, so without this the operator is stuck holding a
 * valid coordinate they cannot save. That was a real dead end on the GPS tab.
 *
 * Uses Geocoding v6 rather than Search Box: the question here is "what address is this spot",
 * which is exactly what v6 reverse geocoding answers. A business name is not wanted -- the pin
 * has been put somewhere deliberately, often a gate or a yard entrance rather than the storefront
 * the POI index knows about, and naming it after whatever shop is nearest would be worse than
 * leaving it alone.
 *
 * Resolves to null when nothing is found, which is normal in the middle of a rail yard. The
 * caller keeps whatever it already had rather than blanking good values for an empty answer.
 */
const reverse = async (
  latitude: number,
  longitude: number
): Promise<GeocodeCandidate | null> => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const response = await axios.get(REVERSE_URL, {
    params: {
      latitude,
      longitude,
      access_token: configProvider.MapboxApiKey,
      limit: 1,
      language: "en",
    },
  });

  const features = response.data?.features;
  if (!Array.isArray(features) || features.length === 0) return null;

  return toAddressCandidate(features[0], 0);
};

const geocodingService = {
  search,
  reverse,
};

export default geocodingService;
